import { Fragment } from "react";
import CButton from "components/CButton";
import useRegisterForm from "./useRegisterForm";
import color from "context/MaterialUITheme/color";
import CTermsDialog from "components/CTermsDialog";
import CPrivacyDialog from "components/CPrivacyDialog";
import CAddressAutocomplete from "components/CAddressAutocomplete";
import {
  Box,
  Link,
  List,
  Button,
  Checkbox,
  MenuItem,
  ListItem,
  useTheme,
  Container,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CDialog from "components/CDialog";
import CLoader from "components/CLoader";
import parseMoney from "utils/parseMoney";
import { passwordMsgHelper } from "CONST";
import { useParams } from "react-router-dom";
import { Controller } from "react-hook-form";
import CTextField from "components/CTextField";
import HTMLtoolTip from "components/HTMLtoolTip";
import CNumberField from "components/CNumberField";
import CPasswordField from "components/CPasswordField";
import ConfirmValuesDialog from "./ConfirmValuesDialog";
import { useInvite } from "context/Invite/useInviteContext";
import FortunaInformation from "components/FortunaInformation";
import CVerificationDialog from "components/CVerificationDialog";
import useUser from "context/UserCustomer/useUserCustomerContext";
import CCommunicationsDialog from "components/CCommunicationsDialog";
import LoanOfficerInformation from "components/LoanOfficerInformation";

const Form: React.FC = () => {
  const user = useUser();
  const theme = useTheme();
  const invite = useInvite();
  const form = useRegisterForm();

  const params = useParams();
  const isABroker = params["brokerName"];
  const sourceId = params["sourceId"];

  const control = form.control;
  const occupancy = form.watch("occupancy");
  const showBorrowerAddress = occupancy && occupancy !== "Primary Residence";
  const disableSubmit = !(
    form.watch("hasAcceptCertify") && form.watch("hasAcceptTerms")
  );
  const { errors } = form.formState;

  return (
    <Fragment>
      {isABroker && <CLoader loading={invite ? false : true} />}
      <Container
        maxWidth="md"
        disableGutters={useMediaQuery(theme.breakpoints.only("xs"))}
      >
        <Box>
          <Typography
            mt={4}
            mb={5}
            variant="h1"
            lineHeight={1}
            color={color.black}
            letterSpacing={-0.8}
            px={{ xs: 2, md: 0 }}
            fontSize={{ xs: "3.5rem", md: "5.2rem" }}
          >
            <span style={{ color: color.purple }}>Hello. </span> Get
            pre-qualified
            <br />
            for your loan here.
          </Typography>
        </Box>

        {invite?.loanOfficer?.firstName && (
          <LoanOfficerInformation
            nmls={invite?.loanOfficer?.NMLSNumber}
            company={invite.broker?.lenderName}
            lastName={invite?.loanOfficer.lastName}
            borrowerName={invite?.firstName}
            avatar={invite.loanOfficer.avatar}
            companyNMLS={invite.broker?.NMLScompany}
            firstName={invite.loanOfficer?.firstName}
          />
        )}
        {sourceId === "fortuna" && <FortunaInformation />}
        <Box
          borderRadius={10}
          bgcolor={color.white}
          sx={{ scrollbarWidth: "none" }}
        >
          <Box px={{ xs: 2, md: 6 }} py={{ xs: 4, md: 5 }}>
            {(invite?.loanOfficer?.firstName || sourceId) && (
              <Typography
                pb={4}
                variant="h6"
                display={"flex"}
                alignSelf={"center"}
                justifyContent={"center"}
                fontFamily={"Pro Display Light"}
              >
                Powered by{" "}
                <span
                  style={{ fontFamily: "Pro Display Semibold", marginLeft: 4 }}
                >
                  <span style={{ color: color.purple }}>NFTY</span>Door.
                </span>
              </Typography>
            )}
            <form onSubmit={form.onSubmit}>
              <Controller
                name={"address"}
                control={control}
                render={({ field }) => (
                  <CAddressAutocomplete
                    placeholder="Start typing your home address and please select from the dropdown."
                    address={field.value}
                    setAddress={field.onChange}
                    sx={{ mb: 3, mt: { xs: 2, md: 0 } }}
                    label="Subject Property Address"
                    //@ts-ignore
                    error={errors?.address?.message}
                  />
                )}
              />
              <Controller
                name={"occupancy"}
                control={control}
                render={({ field }) => (
                  <CTextField
                    select
                    fullWidth
                    tooltip={
                      <HTMLtoolTip
                        title={
                          <Fragment>
                            <Typography color="inherit">
                              Owner Occupied
                            </Typography>
                            {
                              "I/We will occupy the subject property as my/our principal residence within 60 days after the date of closing as required by, and in compliance with, the terms of the Deed of Trust/Mortgage/Security Instrument relating to the subject property. I/We will continue to occupy the property as my/our principal residence for at least one year after the date of occupancy, unless Lender otherwise agrees in writing or extenuating circumstances exist which are beyond my/our control."
                            }
                            <br />
                            <br />
                            <Typography color="inherit">
                              Occupied as a Second Home
                            </Typography>
                            {
                              "I/We will occupy the subject property as my/our second residence as required by, and in compliance with, the terms of the Deed of Trust/Mortgage/Security Instrument relating to the subject property."
                            }
                            <br />
                            <br />
                            <Typography color="inherit">
                              Investment Property - Will Not Occupy
                            </Typography>
                            {"I/We will not occupy the subject property."}
                          </Fragment>
                        }
                      >
                        <Button />
                      </HTMLtoolTip>
                    }
                    label="Occupancy Type"
                    placeholder="Select"
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.occupancy?.message}
                  >
                    <MenuItem value="Primary Residence">
                      Primary Residence
                    </MenuItem>
                    <MenuItem value="Second Home">Second Home</MenuItem>
                    <MenuItem value="Investment Property">
                      Investment Property
                    </MenuItem>
                  </CTextField>
                )}
              />
              {showBorrowerAddress && (
                <Controller
                  name="borrowerAddress"
                  control={control}
                  render={({ field }) => (
                    <CAddressAutocomplete
                      {...field}
                      onChange={undefined}
                      placeholder="Please enter the address of the home you currently live in."
                      address={field.value}
                      setAddress={field.onChange}
                      sx={{ mb: 3, mt: { xs: 2, md: 0 } }}
                      label="Primary home address"
                      //@ts-ignore
                      error={errors?.borrowerAddress?.message}
                    />
                  )}
                />
              )}
              <Controller
                control={control}
                name="homeValue"
                render={({ field }) => (
                  <CNumberField
                    fullWidth
                    label="Home Value"
                    placeholder="$200,000"
                    isNumericString
                    thousandSeparator
                    allowNegative={false}
                    allowEmptyFormatting={false}
                    prefix="$ "
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.homeValue?.message}
                    value={parseMoney(field.value)}
                  />
                )}
              />
              <Controller
                control={control}
                name="currentLoanBalance"
                render={({ field }) => (
                  <CNumberField
                    fullWidth
                    label="Current Loan Balance"
                    placeholder="$100,000"
                    isNumericString
                    thousandSeparator
                    allowNegative={false}
                    allowEmptyFormatting={false}
                    prefix="$ "
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.currentLoanBalance?.message}
                    value={parseMoney(field.value)}
                  />
                )}
              />
              <Controller
                control={control}
                name="requestedNewLoan"
                render={({ field }) => (
                  <CNumberField
                    fullWidth
                    label="Requested Loan Amount"
                    placeholder="$65,000"
                    isNumericString
                    thousandSeparator
                    allowNegative={false}
                    allowEmptyFormatting={false}
                    prefix="$ "
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.requestedNewLoan?.message}
                    value={parseMoney(field.value)}
                  />
                )}
              />
              <Controller
                control={control}
                name="useProceeds"
                render={({ field }) => (
                  <CTextField
                    select
                    fullWidth
                    label="Use of Proceeds"
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.useProceeds?.message}
                  >
                    <MenuItem value="Home Improvement">
                      Home Improvement
                    </MenuItem>
                    <MenuItem value="Debt Consolidation">
                      Debt Consolidation
                    </MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </CTextField>
                )}
              />
              <Controller
                control={control}
                name={"creditScore"}
                render={({ field }) => (
                  <CTextField
                    select
                    fullWidth
                    label="Credit Score"
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.creditScore?.message}
                  >
                    <MenuItem value="780 - 850">780 - 850</MenuItem>
                    <MenuItem value="740 - 779">740 - 779</MenuItem>
                    <MenuItem value="700 - 739">700 - 739</MenuItem>
                    <MenuItem value="680 - 699">680 - 699</MenuItem>
                    <MenuItem value="660 - 679">660 - 679</MenuItem>
                  </CTextField>
                )}
              />
              <Box
                width={"100%"}
                display={"flex"}
                alignItems={"flex-start"}
                flexDirection={{ xs: "column", md: "row" }}
              >
                <Box width={{ xs: "100%", md: "50%" }} mr={{ xs: 0, md: 1 }}>
                  <Controller
                    control={control}
                    name="annualIncome"
                    render={({ field }) => (
                      <CNumberField
                        fullWidth
                        label="Total Household Income"
                        placeholder="$85,000"
                        sx={{ mb: 3 }}
                        isNumericString
                        thousandSeparator
                        allowNegative={false}
                        allowEmptyFormatting={false}
                        prefix="$ "
                        {...field}
                        error={errors?.annualIncome?.message}
                        value={parseMoney(field.value)}
                      />
                    )}
                  />
                </Box>
                <Box width={{ xs: "100%", md: "50%" }} ml={{ xs: 0, md: 1 }}>
                  <Controller
                    control={control}
                    name="employmentType"
                    render={({ field }) => (
                      <CTextField
                        select
                        fullWidth
                        label="Employment Type"
                        sx={{ mb: 3 }}
                        {...field}
                        error={errors?.employmentType?.message}
                      >
                        <MenuItem value="Full Time Employed/ W2">
                          Full Time Employed/ W2
                        </MenuItem>
                        <MenuItem value="Self Employed">Self Employed</MenuItem>
                      </CTextField>
                    )}
                  />
                </Box>
              </Box>
              <Box
                width={"100%"}
                display={"flex"}
                alignItems={"flex-start"}
                flexDirection={{ xs: "column", md: "row" }}
              >
                <Box width={{ xs: "100%", md: "50%" }} mr={{ xs: 0, md: 1 }}>
                  <Controller
                    control={control}
                    name="firstName"
                    render={({ field }) => (
                      <CTextField
                        label="Legal First Name"
                        placeholder="John"
                        fullWidth
                        sx={{ mb: 3 }}
                        {...field}
                        error={errors?.firstName?.message}
                      />
                    )}
                  />
                </Box>

                <Box width={{ xs: "100%", md: "50%" }} ml={{ xs: 0, md: 1 }}>
                  <Controller
                    control={control}
                    name="lastName"
                    render={({ field }) => (
                      <CTextField
                        label="Legal Last Name"
                        placeholder="Doe"
                        fullWidth
                        sx={{ mb: 3 }}
                        {...field}
                        error={errors?.lastName?.message}
                      />
                    )}
                  />
                </Box>
              </Box>
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <CNumberField
                    fullWidth
                    label="Phone Number"
                    placeholder="+1(123)-123-1234"
                    format={
                      process.env.REACT_APP_ENV === "prod"
                        ? "+1###############"
                        : "+###############"
                    }
                    sx={{ mb: 3 }}
                    allowEmptyFormatting
                    {...field}
                    error={errors?.phoneNumber?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <CTextField
                    label="Email Address"
                    placeholder="Type in here"
                    fullWidth
                    sx={{ mb: 3 }}
                    {...field}
                    error={errors?.email?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="password"
                render={({ field }) => (
                  <CPasswordField
                    label="Create Password"
                    placeholder="Type in here"
                    fullWidth
                    helperText={passwordMsgHelper}
                    {...field}
                    error={errors?.password?.message}
                  />
                )}
              />
              <List
                disablePadding
                sx={{ fontSize: { xs: "1rem", md: "1.4rem" } }}
              >
                <ListItem disableGutters alignItems="flex-start" sx={{ mt: 1 }}>
                  <Box display="inline" fontFamily="Pro Display Semibold">
                    <Controller
                      control={control}
                      name="hasAcceptTerms"
                      render={({ field }) => (
                        <Checkbox
                          sx={{ pl: 0 }}
                          onClick={() => field.onChange(!field.value)}
                          {...field}
                        />
                      )}
                    />
                  </Box>
                  <Typography
                    mt={0.8}
                    variant="subtitle1"
                    color={color.black}
                    textAlign={"justify"}
                  >
                    I agree to the{" "}
                    <Link
                      color={color.purple}
                      textAlign={"justify"}
                      sx={{ cursor: "pointer", textDecoration: "none" }}
                      onClick={() => form.setOpenedDialog("terms")}
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      color={color.purple}
                      textAlign={"justify"}
                      sx={{ cursor: "pointer", textDecoration: "none" }}
                      onClick={() => form.setOpenedDialog("privacy")}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                </ListItem>
                <ListItem disableGutters alignItems="flex-start">
                  <Box display="inline" fontFamily="Pro Display Semibold">
                    <Controller
                      control={control}
                      name={"hasAcceptCertify"}
                      render={({ field }) => (
                        <Checkbox
                          sx={{ pl: 0 }}
                          onClick={() => field.onChange(!field.value)}
                          {...field}
                        />
                      )}
                    />
                  </Box>
                  <Typography
                    mt={0.8}
                    variant="subtitle1"
                    color={color.black}
                    textAlign={"justify"}
                  >
                    I certify that by checking this box I have read{" "}
                    <Link
                      color={color.purple}
                      textAlign={"justify"}
                      sx={{ cursor: "pointer", textDecoration: "none" }}
                      onClick={() => form.setOpenedDialog("communications")}
                    >
                      NFTYDoor’s Electronic Communications Policy
                    </Link>
                    , and consent to receive all legally required notices and
                    disclosures and other communications (“Communications”) from
                    NFTYDoor LLC electronically and not on paper. I also confirm
                    I am able to access, view, retain and print all of the
                    Communications NFTYDoor LLC provides me in electronic form.
                  </Typography>
                </ListItem>
              </List>
              <Typography
                my={"1rem"}
                color={color.gray}
                variant="subtitle2"
                textAlign={"justify"}
              >
                By pressing the “I agree” button immediately following this
                notice, I am providing ‘written instructions’ to NFTYDoor under
                the Fair Credit Reporting Act authorizing NFTYDoor to obtain
                information from my personal credit profile or other information
                from Experian. I authorize NFTYDoor to obtain such information
                solely to conduct a pre-qualification for credit and acknowledge
                this will not affect my credit score.
              </Typography>
              <CButton
                fullWidth
                type="submit"
                variant="contained"
                disabled={disableSubmit}
                sx={{ mt: 4, mb: 2, fontSize: 18, textTransform: "inherit" }}
                loading={form.formState.isSubmitting || user.isGettingLoggedIn}
              >
                I Agree
              </CButton>
            </form>
          </Box>
        </Box>
      </Container>
      <CVerificationDialog
        phoneNumber={form.watch("phoneNumber")}
        open={form.openedDialog === "verify"}
        defaultVerificationType="sms"
        email={form.watch("email")}
        onConfirmed={() => {
          form.setOpenedDialog(undefined);
          user.login(form.watch("email"), form.watch("password"));
        }}
      />
      <CDialog
        icon="cancel"
        description={form.errorMessage}
        open={form.openedDialog === "error"}
      >
        <CButton
          fullWidth
          variant="contained"
          onClick={() => form.setOpenedDialog(undefined)}
        >
          Accept
        </CButton>
      </CDialog>
      <ConfirmValuesDialog form={form} />
      <CTermsDialog
        open={form.openedDialog === "terms"}
        handleClose={() => form.setOpenedDialog(undefined)}
      />
      <CPrivacyDialog
        open={form.openedDialog === "privacy"}
        handleClose={() => form.setOpenedDialog(undefined)}
      />
      <CCommunicationsDialog
        open={form.openedDialog === "communications"}
        handleClose={() => form.setOpenedDialog(undefined)}
      />
    </Fragment>
  );
};

export default Form;
